<script setup>
import { computed, onActivated, onDeactivated, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';

import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import SettingsLayout from '../SettingsLayout.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { isTeamAttendant } from './helpers/memberRoles';
import { isArchivedTeamInbox, visibleTeamInboxes } from './helpers/inboxStatus';

import equipesApi from 'dashboard/api/aceleres/equipes';
import AceleresTeamWorkspace from './aceleres/AceleresTeamWorkspace.vue';
import AceleresTeamIaModal from './aceleres/AceleresTeamIaModal.vue';
import AceleresTeamMembersModal from './aceleres/AceleresTeamMembersModal.vue';
import TeamForm from './TeamForm.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { isAdmin } = useAdmin();

// O SettingsWrapper mantem esta pagina em <keep-alive>, e o conteudo teleportado
// de um componente desativado continua no DOM do outlet. Sem este gate o header
// de Equipes reaparece na pagina de Usuarios, que usa o mesmo outlet.
const isPageActive = ref(true);
onActivated(() => {
  isPageActive.value = true;
});
onDeactivated(() => {
  isPageActive.value = false;
});

const pageText = {
  title: 'Equipes',
  subtitle:
    'Organize seu time em equipes, defina canais, acessos, distribuição e configure IA e rodízios para cada equipe.',
  searchPlaceholder: 'Pesquisar equipes...',
  newTeam: 'Nova equipe',
  sidebarLabel: 'Equipes',
  sidebarCreate: 'Criar nova equipe',
  sortLabel: 'Ordenar por:',
  emptyTitle: 'Nenhuma equipe cadastrada',
  emptyDescription:
    'Crie sua primeira equipe para organizar atendentes, configurar canais, permissões, IA e regras de distribuição de conversas.',
  emptyAction: 'Adicionar equipe',
  noResults: 'Nenhuma equipe encontrada para esta busca.',
  deleteModalTitle: 'Tem certeza de que deseja excluir o time?',
  deleteModalDescription:
    'Essa ação removerá a equipe selecionada e suas configurações atuais.',
  deleteModalKeywordLabel: 'Digite excluir para liberar a remoção',
  deleteModalKeywordPlaceholder: 'Escreva excluir',
  deleteModalWarningTitle: 'Esta ação não pode ser desfeita.',
  deleteModalCancel: 'Cancelar',
  deleteModalConfirm: 'Excluir equipe',
  deleteModalDeleting: 'Excluindo...',
  deleteSuccess: 'Equipe excluída com sucesso.',
  deleteError: 'Erro ao excluir equipe.',
  channelsSaved: 'Canais salvos.',
  channelsError: 'Erro ao salvar canais.',
  distributionSaved: 'Distribuição salva.',
  distributionError: 'Erro ao salvar a distribuição.',
  accessSaved: 'Acesso aos atendimentos salvo.',
  accessError: 'Erro ao salvar o acesso aos atendimentos.',
  teamSingular: 'equipe',
  teamPlural: 'equipes',
};

const createTeamText = {
  channelsTitle: 'Canais de atendimento',
  channelsDescription: 'Defina quais canais esta equipe vai atender.',
  channelsPlaceholder: 'Selecionar canais',
  channelsSearch: 'Buscar canal...',
  channelsEmpty: 'Nenhum canal encontrado.',
  distributionTitle: 'Distribuição dos leads',
  distributionTeamTitle: 'Respeitar equipe',
  distributionTeamDescription:
    'A carteira só será respeitada quando o responsável pertencer a esta equipe.',
  distributionAttendantTitle: 'Respeitar atendente',
  distributionAttendantDescription:
    'O lead será enviado ao responsável carteirizado, mesmo que ele pertença a outra equipe.',
  accessTitle: 'Acesso aos atendimentos',
  accessDescription: 'Define quem poderá visualizar os atendimentos da equipe.',
  accessAllTitle: 'Todos',
  accessAllDescription:
    'Membros da equipe veem em Outros, e todos os usuários do sistema veem em Concluídos.',
  accessTeamTitle: 'Equipe',
  accessTeamDescription: 'Visível apenas aos usuários desta equipe.',
  accessAttendantTitle: 'Atendente (por trecho)',
  accessAttendantDescription:
    'Cada atendente vê somente o trecho que atendeu; supervisores acompanham toda a conversa.',
  membersTitle: 'Usuários e supervisores',
  membersDescription:
    'Selecione os usuários da equipe e marque quem será supervisor.',
  membersSearchPlaceholder: 'Buscar por nome ou e-mail',
  membersSelectAll: 'Selecionar todos',
  membersSearch: 'Buscar usuário...',
  membersEmpty: 'Nenhum usuário encontrado.',
  rolesTitle: 'Papel do membro',
  rolesDescription:
    'A participação no rodízio é independente do supervisor. Agentes entram por padrão; administradores precisam ser marcados.',
  rolesEmpty: 'Selecione usuários para definir os papéis.',
  attendantRole: 'Participa do rodízio',
  memberRole: 'Fora do rodízio',
  roleSeparator: '·',
  supervisorRole: 'Supervisor',
  iaTitle: 'IA e Rodízios',
  iaDescription:
    'Ajuste inteligência artificial, rodízios e horários depois que a equipe for criada.',
  iaOpenAfterCreate: 'Abrir configuração de IA e Rodízios após criar a equipe',
};

const createDistributionCards = [
  {
    key: 'respect_team',
    title: createTeamText.distributionTeamTitle,
    description: createTeamText.distributionTeamDescription,
  },
  {
    key: 'respect_attendant',
    title: createTeamText.distributionAttendantTitle,
    description: createTeamText.distributionAttendantDescription,
  },
];

const createAccessCards = [
  {
    key: 'all',
    title: createTeamText.accessAllTitle,
    description: createTeamText.accessAllDescription,
  },
  {
    key: 'team',
    title: createTeamText.accessTeamTitle,
    description: createTeamText.accessTeamDescription,
  },
  {
    key: 'attendant',
    title: createTeamText.accessAttendantTitle,
    description: createTeamText.accessAttendantDescription,
  },
];

const loading = ref({});
const searchQuery = ref('');
const selectedTeamId = ref(null);
const showDeletePopup = ref(false);
const selectedTeamForDelete = ref({});
const iaTeam = ref(null);
const showIaModal = ref(false);
const membersTeam = ref(null);
const teamOverrides = ref({});
const accessLevels = ref({});
const showCreateTeamModal = ref(false);
const createTeamInProgress = ref(false);
const deleteConfirmationInput = ref('');
const createTeamInboxIds = ref([]);
const createTeamDistributionMode = ref('respect_team');
const createTeamAccessLevel = ref('team');
const createTeamMemberIds = ref([]);
const createTeamSupervisorIds = ref([]);
const createTeamRotationEnabledIds = ref([]);
const createTeamMemberSearchQuery = ref('');
const createTeamOpenIaAfterCreate = ref(false);

const teamsListSource = useMapGetter('teams/getTeams');
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const uiFlags = computed(() => getters['teams/getUIFlags'].value);

// Individual própria é o WhatsApp de uma pessoa: não tem equipe dona, não
// entra em rodízio e não pode ser distribuída para um pool. Individual da
// empresa e compartilhada podem (regra de 15/09/2026; de 21/08 a 15/09 a
// individual da empresa também ficava de fora).
//
// `searchText` leva o número além do nome: o nome da instância traz só um
// sufixo ("Isabelly · 5c7cb6"), e a busca por telefone nunca achava o canal.
const inboxOptions = computed(() =>
  (inboxes.value || [])
    .filter(inbox => !isArchivedTeamInbox(inbox))
    .filter(
      inbox =>
        !isAceleresIndividualOwn(inbox.additional_attributes?.connection_type)
    )
    .map(inbox => {
      const phone =
        inbox.additional_attributes?.display_phone_number ||
        inbox.additional_attributes?.phone_number ||
        inbox.phone_number ||
        '';
      return {
        value: inbox.id,
        label: inbox.name,
        searchText: `${inbox.name || ''} ${phone} ${phone.replace(/\D/g, '')}`,
      };
    })
);

const sortedCreateTeamAgents = computed(() =>
  [...(agents.value || [])].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''))
  )
);

const normalizedCreateTeamMemberSearchQuery = computed(() =>
  createTeamMemberSearchQuery.value.trim().toLocaleLowerCase()
);

const visibleCreateTeamAgents = computed(() => {
  if (!normalizedCreateTeamMemberSearchQuery.value) {
    return sortedCreateTeamAgents.value;
  }

  return sortedCreateTeamAgents.value.filter(agent => {
    const name = String(agent.name || '').toLocaleLowerCase();
    const email = String(agent.email || '').toLocaleLowerCase();
    return (
      name.includes(normalizedCreateTeamMemberSearchQuery.value) ||
      email.includes(normalizedCreateTeamMemberSearchQuery.value)
    );
  });
});

const visibleCreateTeamAgentIds = computed(() =>
  visibleCreateTeamAgents.value.map(agent => agent.id)
);

const selectedCreateTeamAgents = computed(() => {
  const selectedIds = new Set(createTeamMemberIds.value);
  return sortedCreateTeamAgents.value.filter(agent =>
    selectedIds.has(agent.id)
  );
});

const allVisibleCreateTeamAgentsSelected = computed(
  () =>
    visibleCreateTeamAgentIds.value.length > 0 &&
    visibleCreateTeamAgentIds.value.every(agentId =>
      createTeamMemberIds.value.includes(agentId)
    )
);

const someVisibleCreateTeamAgentsSelected = computed(
  () =>
    visibleCreateTeamAgentIds.value.some(agentId =>
      createTeamMemberIds.value.includes(agentId)
    ) && !allVisibleCreateTeamAgentsSelected.value
);

const createTeamSelectedCountLabel = computed(() =>
  createTeamMemberIds.value.length === 1
    ? '1 membro selecionado'
    : `${createTeamMemberIds.value.length} membros selecionados`
);

const teamsList = computed(() =>
  (teamsListSource.value || []).map(team => ({
    ...team,
    ...(teamOverrides.value[team.id] || {}),
  }))
);

const sortOptions = [
  { key: 'name', label: 'Nome' },
  { key: 'members', label: 'Membros' },
  { key: 'channels', label: 'Canais' },
];
const sortKey = ref('name');
const isSortMenuOpen = ref(false);

const activeSortLabel = computed(
  () => sortOptions.find(option => option.key === sortKey.value)?.label || ''
);

const applySort = key => {
  sortKey.value = key;
  isSortMenuOpen.value = false;
};

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  const list = query
    ? picoSearch(teamsList.value, query, ['name', 'description'])
    : teamsList.value;
  const sorted = [...list];
  if (sortKey.value === 'members') {
    sorted.sort((a, b) => (b.member_count || 0) - (a.member_count || 0));
  } else if (sortKey.value === 'channels') {
    sorted.sort(
      (a, b) =>
        visibleTeamInboxes(b.inboxes).length -
        visibleTeamInboxes(a.inboxes).length
    );
  } else {
    sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }
  return sorted;
});

const selectedTeam = computed(
  () => teamsList.value.find(team => team.id === selectedTeamId.value) || null
);

const selectedMembers = computed(() =>
  selectedTeamId.value
    ? store.getters['teamMembers/getTeamMembers'](selectedTeamId.value) || []
    : []
);

const selectedAccessLevel = computed(
  () => accessLevels.value[selectedTeamId.value] || 'team'
);

const isDeleteConfirmationValid = computed(
  () => deleteConfirmationInput.value.trim().toLowerCase() === 'excluir'
);
const isDeletingSelectedTeam = computed(
  () => !!loading.value[selectedTeamForDelete.value?.id]
);

const sidebarGridClass = computed(() => 'xl:grid-cols-[22rem_minmax(0,1fr)]');

const formatCount = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : plural}`;

const teamMeta = team =>
  `${formatCount(visibleTeamInboxes(team?.inboxes).length, 'canal', 'canais')} • ${formatCount(
    team?.member_count || 0,
    'membro',
    'membros'
  )}`;

const syncTeamOverride = payload => {
  if (!payload?.id) return;
  teamOverrides.value = {
    ...teamOverrides.value,
    [payload.id]: {
      ...(teamOverrides.value[payload.id] || {}),
      ...payload,
    },
  };
};

const loadTeamMembers = async teamId => {
  if (!teamId) return;
  try {
    await store.dispatch('teamMembers/get', { teamId });
  } catch (error) {
    // ignore
  }
};

const loadAccessLevel = async teamId => {
  if (!teamId) return;
  try {
    const { data } = await equipesApi.getAttendanceAccess(teamId);
    accessLevels.value = {
      ...accessLevels.value,
      [teamId]: data.access_level || 'team',
    };
  } catch (error) {
    accessLevels.value = {
      ...accessLevels.value,
      [teamId]: 'team',
    };
  }
};

const resetCreateTeamDraft = () => {
  createTeamInboxIds.value = [];
  createTeamDistributionMode.value = 'respect_team';
  createTeamAccessLevel.value = 'team';
  createTeamMemberIds.value = [];
  createTeamSupervisorIds.value = [];
  createTeamRotationEnabledIds.value = [];
  createTeamMemberSearchQuery.value = '';
  createTeamOpenIaAfterCreate.value = false;
};

watch(
  filteredTeamsList,
  teams => {
    if (!teams.length) {
      selectedTeamId.value = null;
      return;
    }

    const selectedExists = teams.some(team => team.id === selectedTeamId.value);
    if (!selectedExists) {
      selectedTeamId.value = teams[0].id;
    }
  },
  { immediate: true }
);

watch(
  selectedTeamId,
  teamId => {
    if (!teamId) return;
    loadTeamMembers(teamId);
    loadAccessLevel(teamId);
  },
  { immediate: true }
);

watch(createTeamMemberIds, memberIds => {
  createTeamSupervisorIds.value = createTeamSupervisorIds.value.filter(id =>
    memberIds.includes(id)
  );
  createTeamRotationEnabledIds.value =
    createTeamRotationEnabledIds.value.filter(id => memberIds.includes(id));
});

const selectTeam = team => {
  selectedTeamId.value = team.id;
};

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeamForDelete.value = team;
  deleteConfirmationInput.value = '';
};

const isCreateTeamMemberSelected = agentId =>
  createTeamMemberIds.value.includes(agentId);

const isCreateTeamAttendant = agentId => {
  const agent = sortedCreateTeamAgents.value.find(item => item.id === agentId);
  return isTeamAttendant(agent);
};

const toggleCreateTeamMember = agentId => {
  if (isCreateTeamMemberSelected(agentId)) {
    createTeamMemberIds.value = createTeamMemberIds.value.filter(
      id => id !== agentId
    );
    createTeamRotationEnabledIds.value =
      createTeamRotationEnabledIds.value.filter(id => id !== agentId);
    return;
  }

  createTeamMemberIds.value = [...createTeamMemberIds.value, agentId];
  if (isCreateTeamAttendant(agentId)) {
    createTeamRotationEnabledIds.value = [
      ...new Set([...createTeamRotationEnabledIds.value, agentId]),
    ];
  }
};

const toggleCreateTeamSelectAll = () => {
  if (allVisibleCreateTeamAgentsSelected.value) {
    createTeamMemberIds.value = createTeamMemberIds.value.filter(
      agentId => !visibleCreateTeamAgentIds.value.includes(agentId)
    );
    createTeamRotationEnabledIds.value =
      createTeamRotationEnabledIds.value.filter(
        agentId => !visibleCreateTeamAgentIds.value.includes(agentId)
      );
    return;
  }

  createTeamMemberIds.value = [
    ...new Set([
      ...createTeamMemberIds.value,
      ...visibleCreateTeamAgentIds.value,
    ]),
  ];
  createTeamRotationEnabledIds.value = [
    ...new Set([
      ...createTeamRotationEnabledIds.value,
      ...visibleCreateTeamAgents.value
        .filter(agent => isTeamAttendant(agent))
        .map(agent => agent.id),
    ]),
  ];
};

const isCreateTeamSupervisor = agentId =>
  createTeamSupervisorIds.value.includes(agentId);

const isCreateTeamRotationEnabled = agentId =>
  createTeamRotationEnabledIds.value.includes(agentId);

const setCreateTeamRotationEnabled = (agentId, enabled) => {
  if (!isCreateTeamMemberSelected(agentId)) return;

  if (enabled) {
    createTeamRotationEnabledIds.value = [
      ...new Set([...createTeamRotationEnabledIds.value, agentId]),
    ];
    return;
  }

  createTeamRotationEnabledIds.value =
    createTeamRotationEnabledIds.value.filter(id => id !== agentId);
};

const setCreateTeamMemberRole = (agentId, role) => {
  if (!isCreateTeamMemberSelected(agentId)) return;

  if (role === 'supervisor') {
    createTeamSupervisorIds.value = [
      ...new Set([...createTeamSupervisorIds.value, agentId]),
    ];
    return;
  }

  createTeamSupervisorIds.value = createTeamSupervisorIds.value.filter(
    id => id !== agentId
  );
};

const openCreateTeamModal = () => {
  resetCreateTeamDraft();
  store.dispatch('agents/get');
  showCreateTeamModal.value = true;
};

const closeCreateTeamModal = ({ force = false } = {}) => {
  if (createTeamInProgress.value && !force) return;
  showCreateTeamModal.value = false;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeamForDelete.value = {};
  deleteConfirmationInput.value = '';
};

const handleDeleteBackdrop = () => {
  if (isDeletingSelectedTeam.value) return;
  closeDelete();
};

watch(showCreateTeamModal, (isOpen, _, onCleanup) => {
  const onEsc = event => {
    if (event.key === 'Escape' && !createTeamInProgress.value) {
      closeCreateTeamModal();
    }
  };

  if (isOpen) {
    document.addEventListener('keydown', onEsc);
    onCleanup(() => document.removeEventListener('keydown', onEsc));
    return;
  }

  document.removeEventListener('keydown', onEsc);
});

watch(showDeletePopup, (isOpen, _, onCleanup) => {
  const onEsc = event => {
    if (event.key === 'Escape' && !isDeletingSelectedTeam.value) {
      closeDelete();
    }
  };

  if (isOpen) {
    document.addEventListener('keydown', onEsc);
    onCleanup(() => document.removeEventListener('keydown', onEsc));
    return;
  }

  document.removeEventListener('keydown', onEsc);
});

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(pageText.deleteSuccess);
    return true;
  } catch (error) {
    useAlert(pageText.deleteError);
    return false;
  } finally {
    loading.value[id] = false;
  }
};

const confirmDeletion = async () => {
  if (!isDeleteConfirmationValid.value || isDeletingSelectedTeam.value) return;
  const deleted = await deleteTeam(selectedTeamForDelete.value);
  if (deleted) closeDelete();
};

const createTeam = async data => {
  try {
    createTeamInProgress.value = true;
    const team = await store.dispatch('teams/create', { ...data });
    const { data: updatedTeam } = await equipesApi.update(team.id, {
      team: {
        inbox_ids: createTeamInboxIds.value,
        distribution_mode: createTeamDistributionMode.value,
      },
    });
    syncTeamOverride(updatedTeam);
    await equipesApi.updateAttendanceAccess(
      team.id,
      createTeamAccessLevel.value
    );
    accessLevels.value = {
      ...accessLevels.value,
      [team.id]: createTeamAccessLevel.value,
    };
    if (createTeamMemberIds.value.length) {
      await store.dispatch('teamMembers/create', {
        teamId: team.id,
        agentsList: createTeamMemberIds.value,
      });
      await Promise.all(
        createTeamMemberIds.value
          .filter(
            userId =>
              isCreateTeamRotationEnabled(userId) !==
              isCreateTeamAttendant(userId)
          )
          .map(userId =>
            store.dispatch('teamMembers/setRotationEnabled', {
              teamId: team.id,
              userId,
              enabled: isCreateTeamRotationEnabled(userId),
            })
          )
      );
      await Promise.all(
        createTeamSupervisorIds.value.map(userId =>
          store.dispatch('teamMembers/setRole', {
            teamId: team.id,
            userId,
            role: 'supervisor',
          })
        )
      );
    }
    selectedTeamId.value = team.id;
    searchQuery.value = '';
    closeCreateTeamModal({ force: true });
    await loadTeamMembers(team.id);
    await loadAccessLevel(team.id);
    if (createTeamOpenIaAfterCreate.value) {
      iaTeam.value = updatedTeam || team;
      showIaModal.value = true;
    }
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
  } finally {
    createTeamInProgress.value = false;
  }
};

const updateInboxes = async ids => {
  if (!selectedTeam.value) return;
  try {
    const { data } = await equipesApi.update(selectedTeam.value.id, {
      team: { inbox_ids: ids },
    });
    syncTeamOverride(data);
    useAlert(pageText.channelsSaved);
  } catch (error) {
    useAlert(error?.response?.data?.error || pageText.channelsError);
  }
};

const updateDistributionMode = async mode => {
  if (!selectedTeam.value) return;
  try {
    const { data } = await equipesApi.update(selectedTeam.value.id, {
      team: { distribution_mode: mode },
    });
    syncTeamOverride(data);
    useAlert(pageText.distributionSaved);
  } catch (error) {
    useAlert(error?.response?.data?.error || pageText.distributionError);
  }
};

const updateIndividualCompanyStart = async allowed => {
  // Tarefa #3553 — Validar regras de equipes, instâncias e carteirização no Zoraxy.
  if (!selectedTeam.value) return;
  try {
    const { data } = await equipesApi.update(selectedTeam.value.id, {
      team: { allow_member_individual_company_start: allowed },
    });
    syncTeamOverride(data);
    useAlert('Permissão de uso da Corporativa Individual salva.');
  } catch (error) {
    useAlert(error?.response?.data?.error || pageText.accessError);
  }
};

const updateAccessLevel = async level => {
  if (!selectedTeam.value) return;
  const teamId = selectedTeam.value.id;
  const previousLevel = accessLevels.value[teamId];

  // Atualiza a seleção na hora do clique (otimista) para a UI não parecer
  // travada enquanto o PATCH está em voo; reverte se o backend recusar.
  accessLevels.value = { ...accessLevels.value, [teamId]: level };

  try {
    await equipesApi.updateAttendanceAccess(teamId, level);
    useAlert(pageText.accessSaved);
  } catch (error) {
    accessLevels.value = { ...accessLevels.value, [teamId]: previousLevel };
    useAlert(error?.response?.data?.error || pageText.accessError);
  }
};

const openIa = team => {
  const teamToOpen = team?.id ? team : selectedTeam.value;
  if (!teamToOpen?.id) return;

  iaTeam.value = teamToOpen;
  showIaModal.value = true;
};

const closeIa = () => {
  showIaModal.value = false;
  iaTeam.value = null;
};

const openMembers = team => {
  membersTeam.value = team || selectedTeam.value;
};
</script>

<template>
  <SettingsLayout
    class="scrollbar-thin"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="false"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <Teleport
        v-if="isPageActive"
        defer
        to=".settings-wrapper__teams-header-outlet"
      >
        <header
          class="aceleres-teams-page__header acl-mobile-account-menu-host flex flex-shrink-0 items-center justify-between gap-4 px-0 py-4 transition-[padding] duration-200 ease-out max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-1"
        >
          <!--
            No mobile, titulo, subtitulo e botao ocupam linhas proprias.
            O wrapper define o recuo lateral; o header usa toda a largura
            disponivel sem margens negativas. No desktop o conteudo continua
            na coluna da esquerda e o botao permanece a direita.
          -->
          <div
            class="teams-page__header-content flex min-w-0 flex-1 flex-col max-md:contents"
          >
            <div
              class="teams-page__header-title-row flex min-w-0 items-center gap-3 max-md:order-1 max-md:w-full max-md:flex-none"
            >
              <h1
                class="teams-page__title truncate text-xl font-bold text-n-slate-12 max-md:min-w-0 max-md:flex-1 max-md:pr-[52px] max-md:text-center max-md:text-[25px] max-md:leading-7"
              >
                {{ pageText.title }}
              </h1>
            </div>
            <p
              class="teams-page__subtitle mt-1 text-sm text-n-slate-11 max-md:order-2 max-md:w-full max-md:text-center"
            >
              {{ pageText.subtitle }}
            </p>
          </div>
          <Button
            v-if="isAdmin"
            size="sm"
            icon="i-lucide-plus"
            :label="pageText.newTeam"
            class="teams-page__header-new-team !h-11 !min-h-11 !flex-shrink-0 !rounded-full !px-5 max-md:order-3 max-md:w-full max-md:!px-4"
            @click="openCreateTeamModal"
          />
        </header>
      </Teleport>
    </template>

    <template #body>
      <div
        class="aceleres-teams-page__body flex min-w-0 flex-col gap-4 transition-[padding] duration-200 ease-out"
      >
        <div
          v-if="teamsList.length"
          class="teams-page__toolbar flex items-center gap-3 max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto]"
        >
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="pageText.searchPlaceholder"
            size="sm"
            class="teams-page__search-field group min-h-11 min-w-0 flex-1 max-md:col-span-2 max-md:w-full"
            custom-input-class="teams-page__search-input !h-11 !min-h-11 !rounded-full !border-0 !bg-[#f5f7fb] !pl-10 !text-sm"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-10"
              />
            </template>
          </Input>

          <span
            class="teams-page__count inline-flex h-11 min-h-11 flex-shrink-0 items-center rounded-full bg-[#f5f7fb] px-5 text-sm font-medium text-n-slate-11"
          >
            {{
              formatCount(
                filteredTeamsList.length,
                pageText.teamSingular,
                pageText.teamPlural
              )
            }}
          </span>
          <div class="teams-page__sort relative flex-shrink-0">
            <button
              type="button"
              class="teams-page__sort-trigger inline-flex h-11 min-h-11 items-center gap-2 rounded-full border border-n-weak bg-white px-5 text-sm font-medium text-n-slate-12 transition-colors hover:border-[#bbf7d0] hover:bg-[#f4fbf5]"
              :aria-expanded="isSortMenuOpen"
              aria-haspopup="listbox"
              @click="isSortMenuOpen = !isSortMenuOpen"
            >
              {{ pageText.sortLabel }} {{ activeSortLabel }}
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform"
                :class="{ 'rotate-180': isSortMenuOpen }"
              />
            </button>

            <div
              v-if="isSortMenuOpen"
              class="fixed inset-0 z-10"
              @click="isSortMenuOpen = false"
            />
            <ul
              v-if="isSortMenuOpen"
              class="teams-page__sort-menu absolute right-0 top-[calc(100%+0.5rem)] z-20 min-w-[200px] overflow-hidden rounded-[12px] border border-n-weak bg-white py-1 shadow-[0_12px_40px_rgba(15,23,42,0.12)]"
              role="listbox"
            >
              <li v-for="option in sortOptions" :key="option.key">
                <button
                  type="button"
                  role="option"
                  :aria-selected="sortKey === option.key"
                  class="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-[#f4fbf5]"
                  :class="
                    sortKey === option.key
                      ? 'font-semibold text-[#16a34a]'
                      : 'text-n-slate-12'
                  "
                  @click="applySort(option.key)"
                >
                  {{ option.label }}
                  <span
                    v-if="sortKey === option.key"
                    class="i-lucide-check size-4"
                  />
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div
          v-if="!teamsList.length"
          class="flex min-h-[min(68vh,860px)] items-center justify-center px-6 py-12"
          role="status"
          aria-live="polite"
        >
          <div
            class="flex w-full max-w-[520px] flex-col items-center rounded-[16px] border border-n-weak bg-white px-8 py-10 text-center shadow-[0_12px_40px_rgba(15,23,42,0.06)]"
          >
            <span
              class="mb-5 inline-flex size-16 items-center justify-center rounded-full bg-[#f5f7fb] text-n-slate-11"
            >
              <Icon icon="i-lucide-users-round" class="size-8" />
            </span>
            <h2 class="m-0 text-xl font-semibold text-n-slate-12">
              {{ pageText.emptyTitle }}
            </h2>
            <p
              class="mb-6 mt-3 max-w-[420px] text-sm leading-6 text-n-slate-11"
            >
              {{ pageText.emptyDescription }}
            </p>
            <Button
              v-if="isAdmin"
              size="sm"
              :label="pageText.emptyAction"
              class="!h-11 !min-h-11 !rounded-full !px-6"
              @click="openCreateTeamModal"
            />
          </div>
        </div>

        <div v-else class="grid min-w-0 gap-6" :class="sidebarGridClass">
          <aside
            class="teams-page__sidebar flex min-w-0 flex-col gap-3 xl:relative xl:min-h-0"
          >
            <p
              class="teams-page__sidebar-label mb-0 px-1 text-sm font-semibold text-n-slate-11"
            >
              {{ pageText.sidebarLabel }}
            </p>
            <div
              class="teams-page__list-container rounded-[8px] p-1"
              :class="{
                'xl:absolute xl:inset-0 xl:overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden':
                  selectedTeam,
              }"
            >
              <div
                v-if="filteredTeamsList.length"
                class="teams-page__teams-list flex flex-col gap-3"
              >
                <button
                  v-for="team in filteredTeamsList"
                  :key="team.id"
                  type="button"
                  class="teams-page__team-card relative flex w-full items-center gap-3 overflow-hidden rounded-[8px] border border-transparent px-4 py-4 text-left transition-colors"
                  :class="
                    selectedTeamId === team.id
                      ? 'bg-[#f4fbf5] shadow-[inset_0_0_0_1px_#22c55e,0_8px_24px_rgba(34,197,94,0.08)]'
                      : 'bg-white shadow-[inset_0_0_0_1px_#dbe1ea] hover:shadow-[inset_0_0_0_1px_#cfd7e3]'
                  "
                  @click="selectTeam(team)"
                >
                  <span
                    v-if="selectedTeamId === team.id"
                    class="teams-page__team-card-accent absolute inset-y-0 left-0 w-1 rounded-r-full bg-[#16a34a]"
                    aria-hidden="true"
                  />
                  <span
                    class="inline-flex size-11 flex-shrink-0 items-center justify-center rounded-full"
                    :class="
                      selectedTeamId === team.id
                        ? 'bg-[#e8f7ee] text-[#16a34a]'
                        : 'bg-[#f4fbf5] text-[#22c55e]'
                    "
                  >
                    <Icon icon="i-lucide-users-round" class="size-4" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span
                      class="block truncate text-sm font-semibold text-n-slate-12"
                    >
                      {{ team.name }}
                    </span>
                    <span class="mt-1 block text-sm text-n-slate-11">
                      {{ teamMeta(team) }}
                    </span>
                  </span>
                  <span
                    class="i-lucide-chevron-right size-4 flex-shrink-0"
                    :class="
                      selectedTeamId === team.id
                        ? 'text-[#16a34a]'
                        : 'text-n-slate-10'
                    "
                  />
                </button>
              </div>

              <div
                v-else
                class="mt-4 rounded-[8px] border border-dashed border-n-weak px-4 py-6 text-center text-sm text-n-slate-11"
              >
                {{ pageText.noResults }}
              </div>

              <button
                v-if="isAdmin"
                type="button"
                class="teams-page__sidebar-create mt-3 flex w-full items-center justify-center gap-2 rounded-[8px] border border-dashed border-[#cbd5e1] bg-transparent px-4 py-4 text-sm font-semibold text-[#16a34a] transition-colors hover:border-[#22c55e] hover:bg-[#f4fbf5]"
                @click="openCreateTeamModal"
              >
                <span class="i-lucide-plus size-4" />
                {{ pageText.sidebarCreate }}
              </button>
            </div>
          </aside>

          <AceleresTeamWorkspace
            v-if="selectedTeam"
            :team="selectedTeam"
            :members="selectedMembers"
            :access-level="selectedAccessLevel"
            :inbox-options="inboxOptions"
            @update-inboxes="updateInboxes"
            @update-distribution-mode="updateDistributionMode"
            @update-access-level="updateAccessLevel"
            @update-individual-company-start="updateIndividualCompanyStart"
            @open-ia="openIa"
            @open-members="openMembers(selectedTeam)"
            @delete="openDelete(selectedTeam)"
          />
        </div>
      </div>

      <Teleport to="body">
        <Transition name="team-create-modal">
          <div
            v-if="showCreateTeamModal"
            class="teams-page__create-team-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4 max-md:p-2"
            @click.self="closeCreateTeamModal"
          >
            <div
              class="teams-page__create-team-modal flex max-h-[calc(100vh-2rem)] w-full max-w-5xl min-w-0 flex-col overflow-hidden rounded-[12px] border border-n-weak bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)] max-md:h-[calc(100svh-1rem)] max-md:max-h-[calc(100svh-1rem)]"
              role="dialog"
              aria-modal="true"
              :aria-label="$t('TEAMS_SETTINGS.CREATE_FLOW.CREATE.TITLE')"
            >
              <div
                class="teams-page__create-team-modal-header flex flex-shrink-0 items-start justify-between gap-4 border-b border-n-weak px-6 py-5 max-md:p-4"
              >
                <div>
                  <h2
                    class="teams-page__create-team-modal-title m-0 text-xl font-semibold text-n-slate-12"
                  >
                    {{ $t('TEAMS_SETTINGS.CREATE_FLOW.CREATE.TITLE') }}
                  </h2>
                  <p
                    class="teams-page__create-team-modal-description mb-0 mt-2 text-sm leading-6 text-n-slate-11"
                  >
                    {{ $t('TEAMS_SETTINGS.CREATE_FLOW.CREATE.DESC') }}
                  </p>
                </div>
                <button
                  type="button"
                  class="teams-page__create-team-modal-close inline-flex size-9 items-center justify-center rounded-full border border-n-weak bg-white text-n-slate-11 transition-colors hover:bg-n-slate-2 hover:text-n-slate-12"
                  @click="closeCreateTeamModal"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>

              <div
                class="teams-page__create-team-modal-body scrollbar-thin mx-2 min-h-0 overflow-y-auto overscroll-contain px-4 pb-2 pt-5 max-md:mx-0 max-md:p-4"
              >
                <TeamForm
                  class="teams-page__create-team-modal-form"
                  :on-submit="createTeam"
                  :submit-in-progress="createTeamInProgress"
                  :submit-button-text="$t('TEAMS_SETTINGS.FORM.SUBMIT_CREATE')"
                  submit-actions-class="teams-page__create-team-modal-actions flex w-full items-center justify-end gap-3 px-0 pb-4 pt-2"
                  submit-button-wrapper-class="teams-page__create-team-modal-submit-wrapper w-auto max-md:w-full"
                  submit-button-class="teams-page__create-team-modal-submit !h-11 !rounded-[50px] !px-7 max-md:!w-full"
                >
                  <template #extra-fields>
                    <section
                      class="teams-page__create-team-modal-section teams-page__create-team-modal-channels flex flex-col gap-3 rounded-[8px] border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
                    >
                      <div>
                        <h3 class="m-0 text-base font-semibold text-n-slate-12">
                          {{ createTeamText.channelsTitle }}
                        </h3>
                        <p class="mb-0 mt-1 text-sm text-n-slate-11">
                          {{ createTeamText.channelsDescription }}
                        </p>
                      </div>
                      <TagMultiSelectComboBox
                        v-model="createTeamInboxIds"
                        :options="inboxOptions"
                        :placeholder="createTeamText.channelsPlaceholder"
                        :search-placeholder="createTeamText.channelsSearch"
                        :empty-state="createTeamText.channelsEmpty"
                      />
                    </section>

                    <section
                      class="teams-page__create-team-modal-section teams-page__create-team-modal-distribution flex flex-col gap-3 rounded-[8px] border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
                    >
                      <h3 class="m-0 text-base font-semibold text-n-slate-12">
                        {{ createTeamText.distributionTitle }}
                      </h3>
                      <div class="grid gap-3 lg:grid-cols-2">
                        <button
                          v-for="card in createDistributionCards"
                          :key="card.key"
                          type="button"
                          class="teams-page__create-team-modal-option flex rounded-[8px] border border-transparent px-5 py-5 text-left transition-colors max-md:p-4"
                          :class="
                            createTeamDistributionMode === card.key
                              ? 'bg-[#fff5f5] shadow-[inset_0_0_0_1px_#FF5757]'
                              : 'bg-white shadow-[inset_0_0_0_1px_#dbe1ea] hover:shadow-[inset_0_0_0_1px_#cfd7e3]'
                          "
                          @click="createTeamDistributionMode = card.key"
                        >
                          <div class="flex items-start gap-4">
                            <span
                              class="mt-1 inline-flex size-5 min-h-5 min-w-5 flex-shrink-0 rounded-full border-2"
                              :class="
                                createTeamDistributionMode === card.key
                                  ? 'border-[#FF5757] bg-[#FF5757] shadow-[inset_0_0_0_4px_white]'
                                  : 'border-[#c7d0dc] bg-white'
                              "
                            />
                            <div>
                              <h4
                                class="m-0 text-sm font-semibold text-n-slate-12"
                              >
                                {{ card.title }}
                              </h4>
                              <p
                                class="mb-0 mt-2 text-sm leading-6 text-n-slate-11"
                              >
                                {{ card.description }}
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </section>

                    <section
                      class="teams-page__create-team-modal-section teams-page__create-team-modal-access flex flex-col gap-3 rounded-[8px] border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
                    >
                      <div>
                        <h3 class="m-0 text-base font-semibold text-n-slate-12">
                          {{ createTeamText.accessTitle }}
                        </h3>
                        <p class="mb-0 mt-1 text-sm text-n-slate-11">
                          {{ createTeamText.accessDescription }}
                        </p>
                      </div>
                      <div class="grid gap-3 xl:grid-cols-3">
                        <button
                          v-for="card in createAccessCards"
                          :key="card.key"
                          type="button"
                          class="teams-page__create-team-modal-option flex rounded-[8px] border border-transparent px-5 py-5 text-left transition-colors max-md:p-4"
                          :class="
                            createTeamAccessLevel === card.key
                              ? 'bg-[#fff5f5] shadow-[inset_0_0_0_1px_#FF5757]'
                              : 'bg-white shadow-[inset_0_0_0_1px_#dbe1ea] hover:shadow-[inset_0_0_0_1px_#cfd7e3]'
                          "
                          @click="createTeamAccessLevel = card.key"
                        >
                          <div class="flex flex-col gap-3">
                            <div class="flex items-center gap-3">
                              <span
                                class="inline-flex size-5 min-h-5 min-w-5 flex-shrink-0 rounded-full border-2"
                                :class="
                                  createTeamAccessLevel === card.key
                                    ? 'border-[#FF5757] bg-[#FF5757] shadow-[inset_0_0_0_4px_white]'
                                    : 'border-[#c7d0dc] bg-white'
                                "
                              />
                              <h4
                                class="m-0 text-sm font-semibold text-n-slate-12"
                              >
                                {{ card.title }}
                              </h4>
                            </div>
                            <p class="mb-0 text-sm leading-6 text-n-slate-11">
                              {{ card.description }}
                            </p>
                          </div>
                        </button>
                      </div>
                    </section>

                    <section
                      class="teams-page__create-team-modal-section teams-page__create-team-modal-members flex h-[520px] min-h-0 min-w-0 flex-col gap-3 rounded-[8px] border border-n-weak bg-[#fbfcfd] p-5 max-md:h-[420px] max-md:p-4"
                    >
                      <header>
                        <h3 class="m-0 text-base font-semibold text-n-slate-12">
                          {{ createTeamText.membersTitle }}
                        </h3>
                        <p class="mb-0 mt-1 text-sm text-n-slate-11">
                          {{ createTeamText.membersDescription }}
                        </p>
                      </header>

                      <div
                        class="teams-page__create-team-modal-members-toolbar flex w-full items-stretch gap-3 max-md:flex-col"
                      >
                        <label class="relative min-w-0 flex-[1_1_0]">
                          <span
                            class="i-lucide-search pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                          />
                          <span class="sr-only">
                            {{ createTeamText.membersSearchPlaceholder }}
                          </span>
                          <input
                            v-model="createTeamMemberSearchQuery"
                            type="search"
                            :placeholder="
                              createTeamText.membersSearchPlaceholder
                            "
                            class="reset-base h-11 w-full rounded-[50px] border border-n-weak bg-white py-2 pl-12 pr-4 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-aurora-500"
                          />
                        </label>
                        <div
                          class="teams-page__create-team-modal-selected-count flex h-11 w-60 flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[50px] border border-n-weak bg-white px-4 text-sm font-medium text-n-slate-12 max-md:w-full"
                          aria-live="polite"
                        >
                          <span
                            class="i-lucide-circle-check-big size-4 flex-shrink-0 text-emerald-600"
                          />
                          {{ createTeamSelectedCountLabel }}
                        </div>
                      </div>

                      <div
                        role="button"
                        tabindex="0"
                        class="teams-page__create-team-modal-select-all mt-1 flex w-full items-center gap-3 rounded-[8px] px-4 py-3 text-left transition-colors"
                        @click="toggleCreateTeamSelectAll"
                        @keydown.enter.prevent="toggleCreateTeamSelectAll"
                        @keydown.space.prevent="toggleCreateTeamSelectAll"
                      >
                        <Checkbox
                          :model-value="allVisibleCreateTeamAgentsSelected"
                          :indeterminate="someVisibleCreateTeamAgentsSelected"
                          :title="createTeamText.membersSelectAll"
                          @click.stop
                          @change="toggleCreateTeamSelectAll"
                        />
                        <span class="text-sm font-medium text-n-slate-12">
                          {{ createTeamText.membersSelectAll }}
                        </span>
                      </div>

                      <div
                        class="teams-page__create-team-modal-members-list scrollbar-thin min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain rounded-[8px] border border-n-weak bg-white"
                      >
                        <div
                          v-for="agent in visibleCreateTeamAgents"
                          :key="agent.id"
                          role="button"
                          tabindex="0"
                          class="teams-page__create-team-modal-member-card flex w-full min-w-0 items-center gap-3 border-b border-n-weak px-4 py-3 text-left transition-colors last:border-b-0"
                          :class="
                            isCreateTeamMemberSelected(agent.id)
                              ? 'bg-[#ecfdf3]'
                              : 'bg-white hover:bg-n-slate-2'
                          "
                          @click="toggleCreateTeamMember(agent.id)"
                          @keydown.enter.prevent="
                            toggleCreateTeamMember(agent.id)
                          "
                          @keydown.space.prevent="
                            toggleCreateTeamMember(agent.id)
                          "
                        >
                          <Checkbox
                            :model-value="isCreateTeamMemberSelected(agent.id)"
                            @click.stop
                            @change="() => toggleCreateTeamMember(agent.id)"
                          />
                          <Avatar
                            :src="agent.thumbnail"
                            :name="agent.name"
                            :status="agent.availability_status"
                            :size="36"
                            hide-offline-status
                            rounded-full
                            class="flex-shrink-0"
                          />
                          <span class="min-w-0 flex-1 overflow-hidden">
                            <span
                              class="block truncate text-sm font-semibold text-n-slate-12"
                            >
                              {{ agent.name }}
                            </span>
                            <span
                              class="block truncate text-sm text-n-slate-11"
                            >
                              {{ agent.email || '---' }}
                            </span>
                          </span>
                        </div>

                        <p
                          v-if="!visibleCreateTeamAgents.length"
                          class="mb-0 rounded-[8px] border border-dashed border-n-weak bg-white px-4 py-6 text-center text-sm text-n-slate-11"
                        >
                          {{ createTeamText.membersEmpty }}
                        </p>
                      </div>
                    </section>

                    <section
                      class="teams-page__create-team-modal-section teams-page__create-team-modal-member-roles flex flex-col gap-3 rounded-[8px] border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
                    >
                      <header>
                        <h3 class="m-0 text-base font-semibold text-n-slate-12">
                          {{ createTeamText.rolesTitle }}
                        </h3>
                        <p class="mb-0 mt-1 text-sm text-n-slate-11">
                          {{ createTeamText.rolesDescription }}
                        </p>
                      </header>

                      <div
                        v-if="selectedCreateTeamAgents.length"
                        class="flex flex-col gap-2"
                      >
                        <div
                          v-for="agent in selectedCreateTeamAgents"
                          :key="agent.id"
                          class="teams-page__create-team-modal-role-item flex items-center justify-between gap-3 rounded-[8px] border border-[#dbe1ea] bg-white px-4 py-3 max-md:flex-col max-md:items-stretch max-md:gap-2"
                        >
                          <div class="flex min-w-0 items-center gap-3">
                            <Avatar
                              :src="agent.thumbnail"
                              :name="agent.name"
                              :status="agent.availability_status"
                              :size="32"
                              hide-offline-status
                              rounded-full
                              class="flex-shrink-0"
                            />
                            <span class="min-w-0">
                              <span
                                class="block truncate text-sm font-medium text-n-slate-12"
                              >
                                {{ agent.name }}
                              </span>
                              <span
                                class="block truncate text-xs text-n-slate-11"
                              >
                                {{
                                  isCreateTeamRotationEnabled(agent.id)
                                    ? createTeamText.attendantRole
                                    : createTeamText.memberRole
                                }}
                                <template
                                  v-if="isCreateTeamSupervisor(agent.id)"
                                >
                                  {{ createTeamText.roleSeparator }}
                                  {{ createTeamText.supervisorRole }}
                                </template>
                              </span>
                            </span>
                          </div>

                          <div
                            class="flex flex-shrink-0 items-center gap-2 max-md:w-full"
                          >
                            <button
                              type="button"
                              class="h-8 rounded-full border px-3 text-xs font-medium transition-colors max-md:h-9 max-md:flex-1"
                              :class="
                                isCreateTeamRotationEnabled(agent.id)
                                  ? 'border-[#BCEBD4] bg-[#ECFDF3] text-[#166534]'
                                  : 'border-n-weak bg-[#F1F5F9] text-[#475569] hover:border-[#18A66A] hover:text-[#166534]'
                              "
                              :aria-pressed="
                                isCreateTeamRotationEnabled(agent.id)
                              "
                              @click="
                                setCreateTeamRotationEnabled(
                                  agent.id,
                                  !isCreateTeamRotationEnabled(agent.id)
                                )
                              "
                            >
                              {{
                                isCreateTeamRotationEnabled(agent.id)
                                  ? createTeamText.attendantRole
                                  : createTeamText.memberRole
                              }}
                            </button>
                            <button
                              type="button"
                              class="h-8 rounded-full border px-3 text-xs font-medium transition-colors max-md:h-9 max-md:flex-1"
                              :class="
                                isCreateTeamSupervisor(agent.id)
                                  ? 'border-[#FF5757] bg-[#FF5757] text-white shadow-sm'
                                  : 'border-n-weak bg-white text-[#334155] hover:border-[#FF5757] hover:text-[#FF5757]'
                              "
                              :aria-pressed="isCreateTeamSupervisor(agent.id)"
                              @click="
                                setCreateTeamMemberRole(
                                  agent.id,
                                  isCreateTeamSupervisor(agent.id)
                                    ? 'user'
                                    : 'supervisor'
                                )
                              "
                            >
                              {{ createTeamText.supervisorRole }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <p
                        v-else
                        class="mb-0 rounded-[8px] border border-dashed border-n-weak bg-white px-4 py-6 text-center text-sm text-n-slate-11"
                      >
                        {{ createTeamText.rolesEmpty }}
                      </p>
                    </section>

                    <section
                      class="teams-page__create-team-modal-section teams-page__create-team-modal-ia flex flex-col gap-4 rounded-[8px] border border-[#dceee1] bg-[#f6fbf7] p-5 max-md:p-4"
                    >
                      <div>
                        <h3 class="m-0 text-base font-semibold text-n-slate-12">
                          {{ createTeamText.iaTitle }}
                        </h3>
                        <p class="mb-0 mt-1 text-sm text-n-slate-11">
                          {{ createTeamText.iaDescription }}
                        </p>
                      </div>
                      <label
                        class="teams-page__create-team-modal-ia-toggle flex items-center gap-3 rounded-[8px] border border-[#d7e6da] bg-white px-4 py-3 text-sm font-medium text-n-slate-12"
                      >
                        <input
                          v-model="createTeamOpenIaAfterCreate"
                          type="checkbox"
                          class="size-4 accent-[#FF5757]"
                        />
                        <span>{{ createTeamText.iaOpenAfterCreate }}</span>
                      </label>
                    </section>
                  </template>
                </TeamForm>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </template>

    <AceleresTeamIaModal
      v-if="showIaModal && iaTeam"
      :key="iaTeam?.id || 'ia-modal'"
      :team="iaTeam"
      @close="closeIa"
    />
    <AceleresTeamMembersModal :team="membersTeam" @close="membersTeam = null" />

    <Teleport to="body">
      <Transition name="team-delete-modal">
        <div
          v-if="showDeletePopup"
          class="teams-page__delete-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4 max-md:p-2"
          @click.self="handleDeleteBackdrop"
        >
          <div
            class="teams-page__delete-modal relative flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-y-auto rounded-[16px] border border-n-weak bg-white px-8 pb-7 pt-9 text-center shadow-[0_12px_40px_rgba(15,23,42,0.12)] max-md:max-h-[calc(100dvh-1rem)] max-md:px-4 max-md:pb-5 max-md:pt-6"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="team-delete-modal-title"
          >
            <button
              type="button"
              class="absolute right-5 top-5 z-10 inline-flex size-10 items-center justify-center rounded-full border border-n-weak bg-white text-[#6b7280] transition-colors hover:bg-n-slate-2 hover:text-n-slate-12"
              :disabled="isDeletingSelectedTeam"
              :aria-label="pageText.deleteModalCancel"
              @click="closeDelete"
            >
              <span class="i-lucide-x size-5" />
            </button>

            <div
              class="teams-page__delete-modal-header flex flex-col items-center"
            >
              <span
                class="teams-page__delete-modal-icon mb-5 mt-1 inline-flex size-[68px] flex-shrink-0 items-center justify-center rounded-full bg-[rgba(255,87,87,0.12)]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="#FF5757"
                  aria-hidden="true"
                >
                  <path
                    d="M3 6h18v2H3V6zm2 3h14l-1.5 12.5a2 2 0 0 1-2 1.5h-7a2 2 0 0 1-2-1.5L5 9zm4-5h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1z"
                  />
                </svg>
              </span>

              <h2
                id="team-delete-modal-title"
                class="m-0 text-[22px] font-semibold leading-[1.25] text-n-slate-12"
              >
                {{ pageText.deleteModalTitle }}
              </h2>
              <p class="mb-0 mt-4 text-base leading-7 text-[#6b7280]">
                <span class="block sm:whitespace-nowrap">
                  {{ pageText.deleteModalDescription }}
                </span>
                <span class="mt-2 block text-lg font-semibold text-n-slate-12">
                  {{ selectedTeamForDelete.name }}
                </span>
              </p>
            </div>

            <div
              class="mt-6 rounded-[10px] bg-[rgba(220,38,38,0.08)] px-5 py-4 text-center"
            >
              <p class="m-0 text-base font-semibold text-[#dc2626]">
                {{ pageText.deleteModalWarningTitle }}
              </p>
            </div>

            <div class="mt-5 flex flex-col gap-2.5 text-left">
              <label
                for="team-delete-confirmation"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ pageText.deleteModalKeywordLabel }}
              </label>
              <Input
                id="team-delete-confirmation"
                v-model="deleteConfirmationInput"
                :placeholder="pageText.deleteModalKeywordPlaceholder"
                custom-input-class="!h-12 !rounded-[10px] !border !border-[#e5e7eb] !bg-white !px-4 !text-base focus:!border-[#FF5757] focus:!ring-2 focus:!ring-[rgba(255,87,87,0.1)]"
              />
            </div>

            <div
              class="teams-page__delete-modal-actions flex items-center justify-end gap-3 pt-7 max-md:flex-col-reverse max-md:items-stretch"
            >
              <button
                type="button"
                class="inline-flex min-w-[170px] items-center justify-center rounded-full border border-[#cbd5e1] bg-white px-6 py-3 text-base font-medium text-n-slate-12 shadow-[inset_0_0_0_1px_rgba(203,213,225,0.65)] transition-colors hover:border-[#FF5757] hover:bg-[#f9fafb] hover:text-[#FF5757] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isDeletingSelectedTeam"
                @click="closeDelete"
              >
                {{ pageText.deleteModalCancel }}
              </button>
              <button
                type="button"
                class="inline-flex min-w-[160px] items-center justify-center rounded-full border-0 bg-[#FF5757] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#e04848] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!isDeleteConfirmationValid || isDeletingSelectedTeam"
                @click="confirmDeletion"
              >
                {{
                  isDeletingSelectedTeam
                    ? pageText.deleteModalDeleting
                    : pageText.deleteModalConfirm
                }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </SettingsLayout>
</template>

<style scoped>
.aceleres-teams-page__header {
  width: 100%;
  box-sizing: border-box;
  isolation: isolate;
}

.aceleres-teams-page__body {
  position: relative;
  z-index: 1;
  padding-top: 1.5rem;
}

.teams-page__search-field :deep(.teams-page__search-input) {
  height: 44px !important;
  min-height: 44px !important;
  line-height: 44px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  background: rgba(194, 189, 184, 0.15) !important;
}

.teams-page__count {
  height: 44px !important;
  min-height: 44px !important;
  background: rgba(194, 189, 184, 0.15) !important;
}

.teams-page__new-team-button {
  height: 44px !important;
  min-height: 44px !important;
}

.teams-page__create-team-modal-backdrop,
.teams-page__delete-modal-backdrop {
  isolation: isolate;
}

.teams-page__create-team-modal,
.teams-page__delete-modal {
  position: relative;
  z-index: 10000;
}

.teams-page__create-team-modal :deep(input[type='checkbox']) {
  accent-color: #ff5757;
}

.teams-page__create-team-modal :deep([aria-selected='true'] .i-lucide-check) {
  color: #ff5757;
}

.teams-page__create-team-modal :deep([aria-selected='true']) {
  background: #fff5f5;
}

.teams-page__create-team-modal :deep(.border-n-brand) {
  border-color: #ff5757;
}

.team-create-modal-enter-active,
.team-create-modal-leave-active {
  transition: opacity 0.2s ease;
}

.team-create-modal-enter-from,
.team-create-modal-leave-to {
  opacity: 0;
}

.team-delete-modal-enter-active,
.team-delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.team-delete-modal-enter-from,
.team-delete-modal-leave-to {
  opacity: 0;
}
</style>
